import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';

const LoginForm = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email address is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    const { data, error } = await signIn(formData?.email, formData?.password);

    if (error) {
      setErrors({
        submit: error?.message || 'Invalid credentials. Please try again.'
      });
      setIsLoading(false);
    } else {
      navigate('/dashboard');
    }
  };

  const handleGoogleSignIn = () => {
    setIsLoading(true);
    setTimeout(() => {
      navigate('/dashboard');
    }, 1000);
  };

  return (
    <div className="w-full">
      <div className="bg-card rounded-xl shadow-elevation-4 p-6 md:p-8 lg:p-10">
        <div className="text-center mb-6 md:mb-8">
          <div className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-primary/10 rounded-xl mx-auto mb-4">
            
          </div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-2">
            Welcome Back
          </h1>
          <p className="text-sm md:text-base text-muted-foreground font-caption">
            Sign in to access your financial dashboard
          </p>
        </div>

        <Button
          variant="outline"
          fullWidth
          onClick={handleGoogleSignIn}
          disabled={isLoading}
          className="mb-6"
          iconName="Chrome"
          iconPosition="left"
          iconSize={20}>
          Continue with Google
        </Button>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-8 bg-card text-muted-foreground font-caption">
              Or continue with email
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="email"
            name="email"
            label="Email Address"
            placeholder="demo@rovynbudget.com"
            value={formData?.email}
            onChange={handleChange}
            error={errors?.email}
            required
            disabled={isLoading} />

          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              name="password"
              label="Password"
              placeholder="Enter your password"
              value={formData?.password}
              onChange={handleChange}
              error={errors?.password}
              required
              disabled={isLoading} />

            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-12 top-[38px] flex items-center justify-center w-10 h-10 text-muted-foreground hover:text-foreground transition-smooth"
              aria-label={showPassword ? 'Hide password' : 'Show password'}>
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} className="-mt-5" />
            </button>
          </div>

          {errors?.submit &&
          <div className="bg-error/10 border border-error/20 rounded-lg p-12 flex items-start gap-8">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-2" />
              <p className="text-sm text-error font-caption">{errors?.submit}</p>
            </div>
          }

          <Button
            type="submit"
            variant="default"
            fullWidth
            loading={isLoading}
            disabled={isLoading}
            iconName="LogIn"
            iconPosition="right"
            iconSize={18}>
            {isLoading ? 'Signing In...' : 'Sign In'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => console.log('Forgot password clicked')}
            className="text-sm text-primary hover:text-accent transition-smooth hover:underline font-caption min-h-[44px] inline-flex items-center px-8">
            Forgot your password?
          </button>
        </div>
      </div>
    </div>);

};

export default LoginForm;